if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      document.addEventListener('dieux:product-variant-change', this.handleVariantChange, { signal });
      document.addEventListener('change', this.handleSourcePurchaseChange, { signal });
      document.addEventListener('focusin', this.keepFocusVisible, { signal });
      document.addEventListener('shopify:section:load', this.scheduleSourceRefresh, { signal });
      this.addEventListener('click', this.handleWaitlistClick, { signal });

      this.sourceButtonVisibility = new Map();
      this.sourceObserver = new IntersectionObserver(this.handleSourceIntersections, {
        threshold: [0, 0.01],
      });
      this.sourceMutationObserver = new MutationObserver(this.handleSourceMutations);
      this.sourceMutationObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['hidden', 'disabled', 'data-current-variant-id'],
        childList: true,
        subtree: true,
      });

      this.refreshSourceButtons();
      this.syncPurchaseOption();
      this.syncWaitlistTrigger();

      Promise.all([
        customElements.whenDefined('purchase-options'),
        customElements.whenDefined('variant-discount-price'),
      ]).then(() => {
        if (!signal.aborted) this.syncPurchaseOption();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.sourceObserver?.disconnect();
      this.sourceMutationObserver?.disconnect();
      this.sourceButtonVisibility?.clear();
      this.sourceRefreshQueued = false;
    }

    handleVariantChange = (event) => {
      if (this.dataset.followTemplateVariant !== 'true') return;
      if (String(event.detail?.productId || '') !== this.dataset.productId) return;

      this.applyVariant(event.detail?.variant);
      this.syncPurchaseOption();
    };

    handleSourcePurchaseChange = (event) => {
      const control = event.target;
      if (!(control instanceof Element)) return;
      if (!control.matches('[data-purchase-mode], select[name="selling_plan"]')) return;
      if (!this.isSourceProductForm(control.closest('product-form-component'))) return;

      this.syncPurchaseOption();
    };

    handleSourceIntersections = (entries) => {
      for (const entry of entries) {
        if (!this.sourceButtonVisibility?.has(entry.target)) continue;

        this.sourceButtonVisibility.set(
          entry.target,
          entry.isIntersecting
            && entry.intersectionRatio > 0
            && this.isSourceButtonVisible(entry.target)
        );
      }

      this.updateStickyVisibility();
    };

    handleSourceMutations = (records) => {
      const sourceContentChanged = records.some((record) => (
        !this.contains(record.target)
        && (
          (record.type === 'attributes' && this.isMatchingWaitlist(record.target))
          || [...record.addedNodes, ...record.removedNodes].some(this.containsSourceButtonNode)
        )
      ));
      const sourcePurchaseChanged = records.some((record) => (
        !this.contains(record.target)
        && record.target instanceof Element
        && this.isSourceProductForm(record.target.closest('product-form-component'))
        && (
          record.target.matches('[data-purchase-mode], select[name="selling_plan"]')
          || record.target.closest('select[name="selling_plan"]')
        )
      ));
      if (sourceContentChanged) {
        this.scheduleSourceRefresh();
        this.syncWaitlistTrigger();
      }
      if (sourcePurchaseChanged) this.syncPurchaseOption();
    };

    containsSourceButtonNode = (node) => (
      node instanceof Element
      && (
        node.matches(
          '.js-add-to-cart, product-form-component[data-product-id], product-waitlist-form[data-product-id]'
        )
        || Boolean(node.querySelector(
          'product-form-component[data-product-id] .js-add-to-cart, .js-add-to-cart, product-waitlist-form[data-product-id]'
        ))
      )
    );

    isMatchingWaitlist = (node) => (
      node instanceof HTMLElement
      && node.matches('product-waitlist-form[data-product-id]')
      && node.dataset.productId === this.dataset.productId
    );

    scheduleSourceRefresh = () => {
      if (this.sourceRefreshQueued) return;

      this.sourceRefreshQueued = true;
      queueMicrotask(() => {
        this.sourceRefreshQueued = false;
        if (this.isConnected) this.refreshSourceButtons();
      });
    };

    get sourceScope() {
      return document.querySelector(
        'product-component[data-product-grid-content][data-template-product-state-owner]'
      ) || document;
    }

    get waitlistTarget() {
      return [...this.sourceScope.querySelectorAll('product-waitlist-form[data-product-id]')]
        .find((waitlist) => (
          waitlist.dataset.productId === this.dataset.productId
          && (waitlist.dataset.currentVariantId || waitlist.dataset.initialVariantId)
            === this.dataset.currentVariantId
        )) || null;
    }

    get sourceProductForm() {
      return [...this.sourceScope.querySelectorAll(
        '[data-product-purchase-panel] > product-form-component[data-product-id]'
      )].find((productForm) => this.isSourceProductForm(productForm)) || null;
    }

    isSourceProductForm(productForm) {
      return productForm instanceof HTMLElement
        && productForm.dataset.productId === this.dataset.productId
        && !productForm.closest('sticky-add-to-cart');
    }

    syncPurchaseOption() {
      const sourceProductForm = this.sourceProductForm;
      const stickyProductForm = this.querySelector('product-form-component');
      const stickySellingPlan = stickyProductForm?.querySelector('[data-sticky-selling-plan]');
      if (!(sourceProductForm instanceof HTMLElement)) return;
      if (!(stickyProductForm instanceof HTMLElement)) return;
      if (!(stickySellingPlan instanceof HTMLSelectElement)) return;

      const sourceSellingPlan = sourceProductForm.querySelector(
        'select[name="selling_plan"]:not(:disabled)'
      );
      const selectedPlan = sourceSellingPlan instanceof HTMLSelectElement
        ? sourceSellingPlan.selectedOptions[0]
        : null;

      stickySellingPlan.replaceChildren(
        selectedPlan?.cloneNode(true) || document.createDocumentFragment()
      );
      stickySellingPlan.disabled = !selectedPlan;
      if (selectedPlan) stickySellingPlan.value = selectedPlan.value;

      const variantPrice = selectedPlan?.dataset.planPrice
        || sourceProductForm.dataset.variantPrice;
      if (variantPrice) stickyProductForm.dataset.variantPrice = variantPrice;

      const discountPricing = selectedPlan?.dataset.planDiscountPricing
        || sourceProductForm.dataset.cartLineDiscountPricing;
      if (discountPricing) {
        stickyProductForm.dataset.cartLineDiscountPricing = discountPricing;
      } else {
        delete stickyProductForm.dataset.cartLineDiscountPricing;
      }

      this.syncPurchasePrice(sourceProductForm, stickyProductForm, selectedPlan, discountPricing);
    }

    syncPurchasePrice(sourceProductForm, stickyProductForm, selectedPlan, discountPricing) {
      const stickyPrice = stickyProductForm.querySelector('[data-variant-discount-price]');
      if (!(stickyPrice instanceof HTMLElement)) return;

      if (discountPricing) {
        try {
          stickyPrice.syncPricing?.(JSON.parse(discountPricing));
          return;
        } catch {
          delete stickyProductForm.dataset.cartLineDiscountPricing;
        }
      }

      const sourcePrice = sourceProductForm.querySelector('[data-variant-discount-price]');
      const formattedPrice = selectedPlan?.dataset.planFormattedPrice
        || sourcePrice?.querySelector('[data-variant-discount-price-value]')?.textContent.trim()
        || sourceProductForm.querySelector('[data-button-price]')?.textContent.trim()
        || '';
      const formattedCompareAtPrice = selectedPlan?.dataset.planFormattedCompareAtPrice
        || sourcePrice?.querySelector('[data-variant-discount-price-compare-value]')?.textContent.trim()
        || '';
      const price = selectedPlan?.dataset.planPrice
        || sourcePrice?.dataset.currentPrice
        || sourceProductForm.dataset.variantPrice;
      const compareAtPrice = selectedPlan?.dataset.planCompareAtPrice
        || sourcePrice?.dataset.currentCompareAtPrice
        || '';

      stickyPrice.setPriceOverride?.(
        formattedPrice,
        formattedCompareAtPrice,
        price,
        compareAtPrice
      );
    }

    refreshSourceButtons() {
      const buttons = [...this.sourceScope.querySelectorAll(
        'product-form-component[data-product-id] .js-add-to-cart'
      )].filter((button) => (
        button instanceof HTMLElement
        && button.closest('product-form-component')?.dataset.productId === this.dataset.productId
        && !button.closest('sticky-add-to-cart')
      ));
      const waitlists = [...this.sourceScope.querySelectorAll(
        'product-waitlist-form[data-product-id]'
      )].filter((waitlist) => this.isMatchingWaitlist(waitlist));
      const sources = [...buttons, ...waitlists];

      this.sourceObserver?.disconnect();
      this.sourceButtonVisibility?.clear();

      for (const source of sources) {
        this.sourceButtonVisibility.set(source, this.isSourceButtonVisible(source));
        this.sourceObserver?.observe(source);
      }

      this.updateStickyVisibility();
      this.syncWaitlistTrigger();
    }

    isSourceButtonVisible(button) {
      if (!(button instanceof HTMLElement)) return false;

      const rect = button.getBoundingClientRect();

      return this.isSourceSurfaceRendered(button)
        && rect.bottom > 0
        && rect.right > 0
        && rect.top < window.innerHeight
        && rect.left < window.innerWidth;
    }

    isSourceSurfaceRendered(element) {
      if (!(element instanceof HTMLElement)) return false;

      const style = getComputedStyle(element);
      const rect = element.getBoundingClientRect();
      return style.display !== 'none'
        && style.visibility !== 'hidden'
        && style.visibility !== 'collapse'
        && Number.parseFloat(style.opacity) > 0
        && rect.width > 0
        && rect.height > 0;
    }

    updateStickyVisibility() {
      const sourceButtonIsVisible = [...(this.sourceButtonVisibility?.values() || [])]
        .some(Boolean);
      this.setStickyVisibility(!sourceButtonIsVisible);
    }

    setStickyVisibility(visible) {
      const state = visible ? 'visible' : 'hidden';
      if (this.dataset.stickyState === state) return;

      const bar = this.querySelector('.sticky-add-to-cart__bar');
      if (!(bar instanceof HTMLElement)) return;

      if (visible) {
        bar.inert = false;
        bar.removeAttribute('aria-hidden');
        this.dataset.stickyState = state;
        return;
      }

      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement && bar.contains(activeElement)) {
        const visibleSource = [...(this.sourceButtonVisibility?.entries() || [])]
          .find(([button, isVisible]) => isVisible && this.isSourceButtonVisible(button))?.[0];
        const focusTarget = visibleSource?.matches('product-waitlist-form')
          ? visibleSource.querySelector('input[type="email"]')
          : visibleSource;
        focusTarget?.focus({ preventScroll: true });
      }

      bar.inert = true;
      bar.setAttribute('aria-hidden', 'true');
      this.dataset.stickyState = state;
    }

    handleWaitlistClick = (event) => {
      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) return;

      const trigger = eventTarget.closest('[data-sticky-waitlist-trigger]');
      if (!(trigger instanceof HTMLButtonElement) || !this.contains(trigger)) return;

      const target = this.waitlistTarget;
      if (!this.isSourceSurfaceRendered(target)) return;

      event.preventDefault();
      target.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: (window.DieuxFrame?.prefersReducedMotion() ?? window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 'auto' : 'smooth',
      });
      requestAnimationFrame(() => {
        target.querySelector('input[type="email"]:not([disabled])')?.focus({ preventScroll: true });
      });
    };

    syncWaitlistTrigger() {
      const button = this.querySelector('button[name="add"]');
      if (!(button instanceof HTMLButtonElement)) return;

      const target = this.waitlistTarget;
      const waitlistActive = this.isSourceSurfaceRendered(target);
      this.configureWaitlistTrigger(button, waitlistActive, target);
    }

    configureWaitlistTrigger(button, active, target) {
      if (!(button instanceof HTMLButtonElement)) return;

      const surface = this.dataset.analyticsSurface || 'PDP';
      if (active) {
        const label = target?.querySelector('[data-waitlist-submit-label]')?.textContent.trim()
          || this.dataset.waitlistLabel;
        const buttonText = button.querySelector('[data-button-text]');
        if (buttonText instanceof HTMLElement && label) buttonText.textContent = label;
        button.dataset.textIdle = label || '';
        button.type = 'button';
        button.disabled = false;
        delete button.dataset.disabledByVariant;
        button.classList.remove('js-add-to-cart');
        button.dataset.stickyWaitlistTrigger = '';
        this.setDataValue('clickName', `${surface} - Sticky OOS Notify`, button);
        this.setDataValue('clickWidget', `${surface} - Sticky OOS Notify`);
        if (target?.id) button.setAttribute('aria-controls', target.id);
        button.querySelector('[data-button-price-wrapper]')?.setAttribute('hidden', '');
        return;
      }

      button.type = 'submit';
      button.classList.add('js-add-to-cart');
      delete button.dataset.stickyWaitlistTrigger;
      this.setDataValue('clickName', `${surface} - Sticky Add to Cart`, button);
      this.setDataValue('clickWidget', `${surface} - Sticky Add to Cart`);
      button.removeAttribute('aria-controls');
    }

    keepFocusVisible = (event) => {
      if (this.dataset.stickyState !== 'visible') return;

      const target = event.target;
      const bar = this.querySelector('.sticky-add-to-cart__bar');
      if (!(target instanceof HTMLElement) || !(bar instanceof HTMLElement)) return;
      if (bar.contains(target) || target.closest('dialog[open]')) return;

      window.DieuxFrame.read(() => {
        if (!target.isConnected || !bar.isConnected) return;
        const targetBounds = target.getBoundingClientRect();
        const barTop = bar.getBoundingClientRect().top;
        if (targetBounds.top >= barTop) {
          window.DieuxFrame.write(() => {
            target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
          });
          return;
        }

        const keyboardFocus = document.documentElement.dataset.focusModality === 'keyboard';
        const straddlesBar = targetBounds.bottom > barTop && targetBounds.height < barTop;
        if (!keyboardFocus || !straddlesBar) return;
        const offset = targetBounds.top - (barTop - targetBounds.height) / 2;
        window.DieuxFrame.write(() => {
          window.scrollBy({ top: offset, behavior: 'auto' });
        });
      });
    };

    applyVariant(variant) {
      const productForm = this.querySelector('product-form-component');
      const button = productForm?.querySelector('button[name="add"]');
      const variantId = variant ? String(variant.id) : '';
      this.dataset.currentVariantId = variantId;
      const waitlistActive = Boolean(variant?.waitlistOverrideActive);
      const waitlistTarget = this.waitlistTarget;
      const waitlistState = Boolean(variant && (!variant.available || waitlistActive));
      const waitlistFormActive = waitlistState
        && this.isSourceSurfaceRendered(waitlistTarget);
      const canAdd = Boolean(
        variant
        && variant.available
        && !waitlistFormActive
        && !variant.requiresSellingPlan
      );
      const label = !variant || variant.requiresSellingPlan
        ? this.dataset.unavailableLabel
        : waitlistFormActive
          ? waitlistTarget?.querySelector('[data-waitlist-submit-label]')?.textContent.trim()
            || this.dataset.waitlistLabel
          : variant.available
            ? this.dataset.addLabel
            : this.dataset.soldOutLabel;

      this.setDataValue('clickVariant', variant?.title || '');
      this.setDataValue('clickVariantid', variantId);
      this.setDataValue('variant', variantId);
      this.setDataValue('price', variant?.analyticsPrice || '');

      const variantTitle = this.querySelector('[data-sticky-add-to-cart-variant-title]');
      if (variantTitle instanceof HTMLElement) {
        const subtitle = variant?.variantSubtitle?.trim() || '';
        variantTitle.textContent = subtitle;
        variantTitle.toggleAttribute('hidden', subtitle === '');
      }

      this.replaceFromVariantTemplate(
        '[data-sticky-add-to-cart-media]',
        'template[data-sticky-add-to-cart-variant-media]',
        variantId
      );

      if (!(productForm instanceof HTMLElement)) return;

      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) variantInput.value = variantId;

      productForm.dataset.currentVariantId = variantId;
      productForm.dataset.variantPrice = variant ? String(variant.price) : '';
      if (variant?.url) productForm.dataset.productUrl = variant.url;
      if (variant?.cartLineSubtitle) {
        productForm.dataset.cartLineSubtitle = variant.cartLineSubtitle;
      } else {
        delete productForm.dataset.cartLineSubtitle;
      }

      const addComponent = productForm.querySelector('add-to-cart-component');
      if (addComponent instanceof HTMLElement) addComponent.dataset.variantId = variantId;

      const discountPrice = productForm.querySelector('[data-variant-discount-price]');
      discountPrice?.syncPricing?.(variant?.discountPricing || null);

      const priceWrapper = productForm.querySelector('[data-button-price-wrapper]');
      if (priceWrapper instanceof HTMLElement) {
        priceWrapper.toggleAttribute('hidden', !variant || waitlistState);
      }

      if (button instanceof HTMLButtonElement) {
        this.configureWaitlistTrigger(button, waitlistFormActive, waitlistTarget);
        if (!waitlistFormActive) {
          button.toggleAttribute('disabled', !canAdd || productForm.dataset.busy === 'true');
          button.toggleAttribute('data-disabled-by-variant', !canAdd);
        }
        if (productForm.dataset.busy !== 'true') {
          delete button.dataset.addState;
          button.removeAttribute('aria-busy');
        }
        const buttonText = button.querySelector('[data-button-text]');
        if (buttonText instanceof HTMLElement && label) buttonText.textContent = label;
        button.dataset.textIdle = buttonText?.textContent.trim() || label || '';
      }

      const cartMedia = productForm.querySelector('template[data-cart-line-media]');
      const sourceCartMedia = this.variantTemplate(
        'template[data-sticky-add-to-cart-cart-line-media]',
        variantId
      );
      if (cartMedia instanceof HTMLTemplateElement) {
        cartMedia.content.replaceChildren(
          sourceCartMedia?.content.cloneNode(true) || document.createDocumentFragment()
        );
      }
    }

    setDataValue(key, value, element = this) {
      if (element.dataset[key] !== value) element.dataset[key] = value;
    }

    replaceFromVariantTemplate(targetSelector, templateSelector, variantId) {
      const target = this.querySelector(targetSelector);
      const source = this.variantTemplate(templateSelector, variantId);
      if (!(target instanceof HTMLElement) || !(source instanceof HTMLTemplateElement)) return;

      target.replaceChildren(source.content.cloneNode(true));
    }

    variantTemplate(selector, variantId) {
      return [...this.querySelectorAll(selector)]
        .find((template) => template.dataset.variantId === variantId);
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
